import { useState, useRef, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { toast } from 'sonner'
import { getDeviceId } from '../lib/deviceId'

const BUCKET = 'reunioes'
const SEGMENT_INTERVAL = 5 * 60 * 1000 // 5 minutes
const RETRY_INTERVAL = 30 * 1000 // 30 seconds
const MAX_UPLOAD_RETRIES = 10

/**
 * Caminho de um segmento de áudio no bucket `reunioes`.
 * Esquema por `reuniao.id` (PK global) — desacoplado de `projeto_id`, o que permite
 * gravar reuniões avulsas (sem projeto). process-reuniao deriva o diretório deste path.
 *
 * O nível do `deviceId` existe porque cada aparelho zera o próprio contador de
 * segmentos: sem ele, notebook e celular gravando a mesma reunião escrevem ambos
 * `segmento_000`, e a descoberta no servidor ordena por nome e costura as duas
 * gravações como se fossem uma só. Com a pasta, as duas ficam separáveis mesmo
 * quando a prevenção no cliente falha (aba suspensa, sem rede, reload).
 */
export function buildSegmentPath(
  reuniaoId: string | number, deviceId: string, segNum: number, ext: string, ts: number
): string {
  return `${reuniaoId}/${deviceId}/segmento_${String(segNum).padStart(3, '0')}_${ts}.${ext}`
}

type RecState = 'idle' | 'recording' | 'paused-rec' | 'uploading' | 'done'

interface AudioRecorderParams {
  reuniaoId: string | number | null
  updateMeeting: (data: { storage_audio: string }) => Promise<unknown>
  broadcastRecState?: (state: string) => void
  /** Impede o início automático da gravação (reunião do Meet, ou outro aparelho já gravando). */
  disabled?: boolean
  /**
   * Gravação deliberada de apoio, com outro aparelho já gravando a mesma reunião.
   * O áudio sobe e fica guardado, mas não dispara transcrição progressiva nem
   * assume `storage_audio` — senão as duas gravações se intercalam na transcrição.
   */
  secondary?: boolean
  /** Avisa a camada de realtime que este aparelho começou/parou de gravar. */
  onRecordingChange?: (recording: boolean) => void
}

interface PendingUpload {
  blob: Blob
  path: string
  segNum: number
  retries: number
  ext: string
  /** Segundos de áudio efetivamente gravados neste segmento. */
  segSeconds: number
}

export default function useAudioRecorder({ reuniaoId, updateMeeting, broadcastRecState, disabled, secondary, onRecordingChange }: AudioRecorderParams) {
  const deviceId = useRef<string>(getDeviceId())
  // Lidos dentro de callbacks que não são recriados a cada render — ref evita capturar valor velho.
  const secondaryRef = useRef(!!secondary)
  useEffect(() => { secondaryRef.current = !!secondary }, [secondary])
  const onRecordingChangeRef = useRef(onRecordingChange)
  useEffect(() => { onRecordingChangeRef.current = onRecordingChange }, [onRecordingChange])

  const [recState, setRecState] = useState<RecState>('idle')
  const [recSecs,  setRecSecs]  = useState(0)
  const [lastSaved, setLastSaved] = useState<number | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [transcribedCount, setTranscribedCount] = useState(0)

  const mediaRef      = useRef<MediaRecorder | null>(null)
  const streamRef     = useRef<MediaStream | null>(null)
  const chunksRef     = useRef<Blob[]>([])
  const recTimer      = useRef<ReturnType<typeof setInterval> | null>(null)
  const cancelledRef  = useRef(false)
  const rotatingRef   = useRef(false)
  const segmentRef    = useRef(0)
  const segmentosRef  = useRef<string[]>([])
  const transcribedRef = useRef<Set<number>>(new Set())
  const segmentTimer  = useRef<ReturnType<typeof setInterval> | null>(null)
  const recStateRef   = useRef<RecState>('idle')
  // Espelho de `recSecs` em ref, e marca do início do segmento atual. Servem para
  // medir a duração REAL de cada segmento: o servidor precisa dela para decidir
  // se um segmento é mudo. Presumir 300s descartaria fala de segmento curto
  // (o último de qualquer gravação é parcial, e pausa encurta os outros).
  const recSecsRef    = useRef(0)
  const segStartSecs  = useRef(0)
  const pendingUploads = useRef<PendingUpload[]>([])
  const retryTimer    = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => { recStateRef.current = recState }, [recState])

  // ── Retry pending uploads every 30s ──
  const retryPending = useCallback(async () => {
    if (pendingUploads.current.length === 0) return
    const stillPending = []
    for (const item of pendingUploads.current) {
      try {
        const { error } = await supabase.storage.from(BUCKET).upload(item.path, item.blob, { upsert: true })
        if (error) throw error
        segmentosRef.current.push(item.path)
        setLastSaved(Date.now())
        if (item.segNum === 0 && !secondaryRef.current) {
          await updateMeeting({ storage_audio: item.path }).catch(() => {})
        }
        // Fire progressive transcription
        triggerSegmentTranscription(item.path, item.segNum, item.segSeconds)
      } catch {
        item.retries++
        if (item.retries >= MAX_UPLOAD_RETRIES) {
          toast.error(`Segmento ${item.segNum} descartado após ${MAX_UPLOAD_RETRIES} tentativas`)
        } else {
          stillPending.push(item)
        }
      }
    }
    pendingUploads.current = stillPending
    setPendingCount(stillPending.length)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Fire-and-forget progressive transcription for a segment
  function triggerSegmentTranscription(segmentPath: string, segmentOrder: number, segSeconds: number) {
    if (!reuniaoId) return
    // Gravação de apoio não transcreve: o áudio fica guardado, mas quem monta a
    // transcrição é o aparelho principal. Sem isso os dois mandam segment_order 0
    // e o pipeline descarta um deles em silêncio.
    if (secondaryRef.current) return
    supabase.functions.invoke('process-reuniao', {
      body: {
        reuniao_id: reuniaoId, mode: 'segment',
        segment_path: segmentPath, segment_order: segmentOrder,
        segment_seconds: segSeconds,
      },
    }).then(({ data }) => {
      if (data?.ok && !transcribedRef.current.has(segmentOrder)) {
        transcribedRef.current.add(segmentOrder)
        setTranscribedCount(transcribedRef.current.size)
      }
    }).catch(e => console.warn('Progressive transcription trigger failed:', e))
  }

  // Start/stop retry timer based on pending count
  useEffect(() => {
    if (pendingCount > 0 && !retryTimer.current) {
      retryTimer.current = setInterval(retryPending, RETRY_INTERVAL)
    } else if (pendingCount === 0 && retryTimer.current) {
      clearInterval(retryTimer.current)
      retryTimer.current = null
    }
    return () => { clearInterval(retryTimer.current); retryTimer.current = null }
  }, [pendingCount, retryPending])

  // Cleanup on unmount: stop recorder + release stream to avoid orphaned recordings
  useEffect(() => {
    return () => {
      clearInterval(recTimer.current)
      clearInterval(segmentTimer.current)
      clearInterval(retryTimer.current)
      // Solta a reserva de gravação: sem isto, fechar a aba deixaria os outros
      // aparelhos achando que ainda há gravação em curso.
      onRecordingChangeRef.current?.(false)
      // Stop MediaRecorder gracefully — onstop handler will upload the final segment
      if (mediaRef.current && mediaRef.current.state !== 'inactive') {
        rotatingRef.current = false
        mediaRef.current.stop()
      } else {
        // If no active recorder, just release the stream
        streamRef.current?.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
    }
  }, [])

  // Auto-start recording.
  // `disabled` está nas dependências de propósito: ele começa true enquanto o
  // presence ainda não sincronizou e cai para false depois. Sem isso a gravação
  // nunca começaria — e com deps só em `reuniaoId` a espera viraria um bug pior
  // que o que ela conserta. `autoStarted` garante que só dispara uma vez.
  const autoStarted = useRef(false)
  useEffect(() => {
    if (reuniaoId && !autoStarted.current && !disabled) {
      autoStarted.current = true
      startRecording()
    }
  }, [reuniaoId, disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on reuniao change
  useEffect(() => {
    if (!reuniaoId) return
    setRecState('idle')
    setRecSecs(0)
  }, [reuniaoId])

  function startNewRecorder(stream: MediaStream) {
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg'
    const rec = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 })
    mediaRef.current = rec
    chunksRef.current = []
    rec.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
    rec.onstop = handleRecordingStop
    rec.start(1000)
  }

  /**
   * Primeiro número de segmento livre na pasta deste aparelho.
   *
   * Recarregar a página no meio de uma reunião zerava o contador, e a gravação
   * voltava a escrever `segmento_000` na mesma pasta. Resultado: dois trechos
   * diferentes disputando o mesmo índice, que é o mesmo estrago que dois
   * aparelhos causavam. Continuar a numeração fecha isso.
   */
  async function proximoSegmentoLivre(): Promise<number> {
    if (!reuniaoId) return 0
    try {
      const dir = `${reuniaoId}/${deviceId.current}`
      const { data } = await supabase.storage.from(BUCKET)
        .list(dir, { limit: 1000, sortBy: { column: 'name', order: 'asc' } })
      const indices = (data || [])
        .map(f => /^segmento_(\d+)_/.exec(f.name)?.[1])
        .filter((n): n is string => !!n)
        .map(Number)
      return indices.length ? Math.max(...indices) + 1 : 0
    } catch {
      return 0  // Falha de listagem não pode impedir a gravação de começar.
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      cancelledRef.current = false
      segmentRef.current = await proximoSegmentoLivre()
      recSecsRef.current = 0
      segStartSecs.current = 0
      segmentosRef.current = []
      startNewRecorder(stream)
      setRecState('recording'); setRecSecs(0)
      recTimer.current = setInterval(() => { recSecsRef.current += 1; setRecSecs(d => d + 1) }, 1000)
      broadcastRecState?.('recording')
      onRecordingChange?.(true)
      segmentTimer.current = setInterval(() => rotateRecording(), SEGMENT_INTERVAL)
    } catch { toast.error('Permissão de microfone negada') }
  }

  function rotateRecording() {
    if (recStateRef.current !== 'recording') return
    rotatingRef.current = true
    mediaRef.current?.stop()
  }

  function stopRecording() {
    clearInterval(recTimer.current)
    clearInterval(segmentTimer.current)
    rotatingRef.current = false
    mediaRef.current?.stop()
    broadcastRecState?.('idle')
    onRecordingChange?.(false)
  }

  function releaseStream() {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }

  async function handleRecordingStop() {
    if (cancelledRef.current) { releaseStream(); return }

    const isRotation = rotatingRef.current
    rotatingRef.current = false

    if (!isRotation) setRecState('uploading')

    const mimeType = chunksRef.current[0]?.type || 'audio/webm'
    const ext = mimeType.includes('ogg') ? 'ogg' : 'webm'
    const blob = new Blob(chunksRef.current, { type: mimeType })

    if (blob.size < 1024) {
      if (!isRotation) { setRecState('done'); releaseStream() }
      else if (streamRef.current) { chunksRef.current = []; startNewRecorder(streamRef.current) }
      return
    }

    const segNum = segmentRef.current++
    const path = buildSegmentPath(reuniaoId!, deviceId.current, segNum, ext, Date.now())
    // Duração real deste segmento: o contador só avança gravando, então pausa não
    // entra na conta. O servidor usa isto para decidir se o segmento é mudo.
    const segSeconds = Math.max(0, recSecsRef.current - segStartSecs.current)
    segStartSecs.current = recSecsRef.current

    try {
      const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { upsert: true })
      if (error) throw error
      segmentosRef.current.push(path)
      setLastSaved(Date.now())

      // storage_audio aponta para o primeiro segmento deste aparelho; o servidor
      // deriva o diretório a partir dele. Gravação de apoio não sobrescreve o
      // ponteiro da gravação principal.
      if (segNum === 0 && !secondaryRef.current) {
        await updateMeeting({ storage_audio: path })
      }
      // Fire progressive transcription for this segment
      triggerSegmentTranscription(path, segNum, segSeconds)
    } catch (e) {
      console.error('Erro upload segmento:', e)
      // Keep blob in memory for retry
      pendingUploads.current.push({ blob, path, segNum, retries: 0, ext, segSeconds })
      setPendingCount(pendingUploads.current.length)
      if (!isRotation) toast.warning('Upload falhou — tentando novamente em 30s')
    }

    if (isRotation && streamRef.current) {
      chunksRef.current = []
      startNewRecorder(streamRef.current)
    } else {
      setRecState('done')
      releaseStream()
    }
  }

  function pauseRecording() {
    if (recState === 'recording') {
      mediaRef.current?.pause()
      clearInterval(recTimer.current)
      clearInterval(segmentTimer.current)
      setRecState('paused-rec')
    }
  }

  function resumeRecording() {
    if (recState === 'paused-rec') {
      mediaRef.current?.resume()
      recTimer.current = setInterval(() => { recSecsRef.current += 1; setRecSecs(d => d + 1) }, 1000)
      segmentTimer.current = setInterval(() => rotateRecording(), SEGMENT_INTERVAL)
      setRecState('recording')
    }
  }

  function cancelRecording() {
    cancelledRef.current = true
    clearInterval(recTimer.current)
    clearInterval(segmentTimer.current)
    mediaRef.current?.stream?.getTracks().forEach(t => t.stop())
    mediaRef.current?.stop()
    broadcastRecState?.('idle')
    onRecordingChange?.(false)
  }

  const isRec = recState === 'recording' || recState === 'paused-rec'

  /** Wait for last segment upload + all pending retries to complete */
  async function waitForUpload() {
    if (!isRec) {
      // Even if not recording, flush pending uploads before encerrar
      if (pendingUploads.current.length > 0) await retryPending()
      return
    }
    stopRecording()
    // Wait for current segment upload to finish
    await new Promise(resolve => {
      const check = setInterval(() => {
        if (recStateRef.current === 'done' || recStateRef.current === 'idle') {
          clearInterval(check); resolve()
        }
      }, 200)
      setTimeout(() => { clearInterval(check); resolve() }, 10000)
    })
    // Flush any remaining pending uploads
    if (pendingUploads.current.length > 0) {
      toast.info(`Enviando ${pendingUploads.current.length} segmento(s) pendente(s)...`)
      await retryPending()
    }
  }

  return {
    recState, recSecs, lastSaved, isRec,
    pendingCount, transcribedCount,
    segmentosRef, deviceId: deviceId.current,
    startRecording, stopRecording, pauseRecording, resumeRecording,
    cancelRecording, waitForUpload,
  }
}
